import React, { useRef } from 'react';
import DigitalCardLayerStyled from 'src/components/_common/DigitalCard/_components/DigitalCardLayer/index.styled';
import type { VideoLayer } from 'src/components/_common/DigitalCard/_components/DigitalCardLayer/types';
import { useDigitalCardActions, useDigitalCardConfig } from 'src/components/_common/DigitalCard/_hooks/useDigitalCard';
import { useVideoLoad } from 'src/components/_common/DigitalCard/_hooks/useVideoLoad';
import styled from 'styled-components';

interface VideoLayerProps extends VideoLayer {
  faceIdx: number;
}

/**
 * [디지털 카드 비디오 레이어 컴포넌트]
 *
 * @description
 * 비디오 레이어를 디지털 카드에 표시하는 컴포넌트입니다.
 * 비디오 로딩 상태를 관리하고, 비디오 로딩 완료 시 비디오 로딩 완료 핸들러를 호출합니다.
 *
 * @param {VideoLayer} props - 비디오 레이어 컴포넌트의 속성
 * ```ts
 * {
 *   idx: number; // 레이어의 고유 식별자
 *   name: string; // 레이어의 이름
 *   type: 'video'; // 레이어 타입
 *   zIndex: number; // 레이어의 z-index 값
 *   translateZ: number; // 레이어의 z축 변환 값
 *   borderRadius: number; // 레이어의 모서리 둥글기
 *   videoSrc: string; // 비디오 소스 URL
 *   faceIdx: number; // 디지털 카드의 면 인덱스
 * }
 * ```
 */
export const DigitalCardVideoLayer = ({
  name,
  zIndex,
  translateZ,
  borderRadius,
  videoSrc,
}: VideoLayerProps) => {
  const { videoLoadRetryCount, videoLoadDelayTime } = useDigitalCardConfig();
  const { handleResourceLoadComplete, handleResourceLoadError } = useDigitalCardActions();
  const videoRef = useRef<HTMLVideoElement>(null);

  useVideoLoad({
    videoRef,
    src: videoSrc,
    onLoadComplete: () => {
      handleResourceLoadComplete(videoSrc);
    },
    onLoadError: () => {
      handleResourceLoadError();
    },
    retryCount: videoLoadRetryCount,
    delayTime: videoLoadDelayTime,
  });

  return (
    <VideoLayerWrapper $zIndex={zIndex} $translateZ={translateZ} $borderRadius={borderRadius}>
      <VideoElement
        title={`[카드 레이어] ${name}`}
        src={videoSrc}
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        disablePictureInPicture
        controlsList="nodownload nofullscreen noremoteplayback"
      />
    </VideoLayerWrapper>
  );
};

const VideoLayerWrapper = styled(DigitalCardLayerStyled.BaseLayerWrapper)`
  overflow: ${({ $borderRadius }) => (!$borderRadius ? 'visible' : 'hidden')};
  ${({ $borderRadius }) => ($borderRadius ? `border-radius: ${$borderRadius}px;` : '')}
`;

const VideoElement = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
`;
